import clsx from "clsx";
import React from "react";
import { FormattedMessage } from "react-intl";
import Skeleton from "react-loading-skeleton";
import { Link } from "react-router-dom";

import { MeetingState, type TMeeting, useGetDoctorMeetingsQuery } from "~/api";
import { PersonName } from "~/components/common/PersonName";
import { Button } from "~/components/ui/button";

import {
  EmptyDisplay,
  ExpertImage,
  ExpertName,
  formatStartDate,
  StateBadge,
  ZoomButton,
} from "./meetings-shared";

// TODO: try a menu of buttons with reschedule and cancel
export function MeetingsList() {
  const meetingsQuery = useGetDoctorMeetingsQuery();

  if (meetingsQuery.isUninitialized || meetingsQuery.isLoading) {
    return (
      <ul role="list" className="tw-list-none tw-space-y-4 tw-p-0">
        {Array.from({ length: 3 }, (_, index) => (
          <Meeting key={index} meeting={undefined} displayHr={index < 2} />
        ))}
      </ul>
    );
  }

  if (meetingsQuery.isError) {
    return (
      <EmptyDisplay
        title="Не удалось загрузить записи"
        description={<FormattedMessage id="events.error.description" />}
        isError
      />
    );
  }

  if (meetingsQuery.data.length == 0) {
    return (
      <EmptyDisplay
        title="Записи отсутствуют"
        description="Здесь будет отображаться история Ваших записей к клиническим экспертам"
        link={{ to: "/pages/meetings", children: "Записаться" }}
      />
    );
  }

  return (
    <ul role="list" className="tw-list-none tw-space-y-4 tw-p-0">
      {meetingsQuery.data.map((meeting, index, array) => (
        <Meeting key={meeting.id} meeting={meeting} displayHr={index < array.length - 1} />
      ))}
    </ul>
  );
}

function Meeting({ meeting, displayHr }: { meeting: TMeeting | undefined; displayHr: boolean }) {
  return (
    <>
      <li className="tw-flex tw-flex-col tw-gap-3">
        <div className="tw-flex tw-items-center tw-justify-between tw-gap-4 max-sm:tw-flex-col max-sm:tw-items-start">
          {meeting ? (
            <span className="tw-font-semibold tw-text-black">
              {formatStartDate(meeting.start_time)}
            </span>
          ) : (
            <Skeleton className="tw-rounded-md" width={200} height={20} />
          )}

          <StateBadge meeting={meeting} className="tw-ml-auto" short />
        </div>

        <MeetingMobile meeting={meeting} />
        <MeetingDesktop meeting={meeting} />
      </li>

      {displayHr ? <hr className="tw-mb-0 tw-mt-4" /> : null}
    </>
  );
}

function MeetingMobile({ meeting }: { meeting: TMeeting | undefined }) {
  return (
    <div className="tw-flex tw-flex-col tw-gap-4 sm:tw-hidden">
      <div
        className="tw-grid tw-items-center"
        style={{
          gridTemplateColumns: "75px 1fr",
          gridTemplateRows: "75px 75px",
          columnGap: 20,
          rowGap: 8,
        }}
      >
        <ExpertImage expert={meeting?.expert} />
        <ExpertName meeting={meeting} />

        <div />
        <PatientNames meeting={meeting} />
      </div>

      <ActionButtons meeting={meeting} uppercase />
    </div>
  );
}

function MeetingDesktop({ meeting }: { meeting: TMeeting | undefined }) {
  return (
    <div className="tw-flex tw-items-center tw-gap-3 max-sm:tw-hidden">
      <ExpertImage expert={meeting?.expert} />
      <ExpertName meeting={meeting} className="tw-w-[175px] sm:tw-mr-8" />
      <PatientNames meeting={meeting} />
      <ActionButtons meeting={meeting} />
    </div>
  );
}

function ActionButtons({
  meeting,
  uppercase = false,
}: {
  meeting: TMeeting | undefined;
  uppercase?: boolean;
}) {
  const className = clsx({ "tw-uppercase": uppercase });

  return (
    <div className="tw-flex tw-items-start tw-gap-2">
      {meeting == undefined || (meeting.state == MeetingState.NEW && !meeting.is_finished) ? (
        <ZoomButton meeting={meeting} />
      ) : null}

      {meeting ? (
        <Button color="secondary" rounded className={className} asChild>
          <Link to={`/pages/meetings/enrolled/${meeting.id}`}>Описание записи</Link>
        </Button>
      ) : (
        <Skeleton className="tw-rounded-md tw-leading-5" width={150} height={36} />
      )}
    </div>
  );
}

function PatientNames({ meeting }: { meeting: TMeeting | undefined }) {
  return (
    <div className={clsx("tw-flex tw-flex-col tw-items-start tw-gap-1", "tw-grow")}>
      {meeting ? (
        <>
          <span className="tw-text-gray-400">
            {meeting.patients.length == 1 ? "Пациент" : "Пациенты"}
          </span>

          <div className="tw-flex tw-flex-col">
            {meeting.patients.map((patient) => (
              <Link key={patient.patient_id} to={`/pages/patient/${patient.patient_id}`}>
                <PersonName person={patient} useMiddleName isDoctor /> {patient.patient_id}
              </Link>
            ))}
          </div>
        </>
      ) : (
        <>
          <Skeleton className="tw-rounded" width={200} />
          <Skeleton className="tw-rounded" width={200} />
        </>
      )}
    </div>
  );
}
